import type {
  FormBuilderField,
  FieldValidationConfig,
  VueformFieldSchema,
  VueformSchema,
} from '../types/form-builder-types'

/**
 * Generate a field name from a label (snake_case)
 */
export function generateFieldName(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
}

/**
 * Build Vueform rules string from validation config
 */
export function buildRulesString(validation?: FieldValidationConfig): string {
  if (!validation) return ''

  const rules: string[] = []

  if (validation.required) rules.push('required')
  if (validation.email) rules.push('email')
  if (validation.url) rules.push('url')
  if (validation.numeric) rules.push('numeric')
  if (validation.minLength) rules.push(`min:${validation.minLength}`)
  if (validation.maxLength) rules.push(`max:${validation.maxLength}`)
  if (validation.minValue) rules.push(`min:${validation.minValue}`)
  if (validation.maxValue) rules.push(`max:${validation.maxValue}`)
  if (validation.pattern) rules.push(`regex:${validation.pattern}`)

  return rules.join('|')
}

/**
 * Convert FormBuilderField to Vueform field schema
 */
export function convertToVueformField(field: FormBuilderField): VueformFieldSchema {
  const schema: VueformFieldSchema = {
    type: field.type,
    label: field.label,
  }

  if (field.placeholder) schema.placeholder = field.placeholder
  if (field.helpText) schema.help = field.helpText
  if (field.defaultValue !== undefined) schema.default = field.defaultValue
  if (field.onUpgrade) schema.onUpgrade = field.onUpgrade
  if (field.columns) schema.columns = field.columns
  if (field.readonly) schema.readonly = field.readonly

  // Build rules string
  const rules = buildRulesString(field.validation)
  if (rules) schema.rules = rules

  // Handle field-specific configurations
  if (field.type === 'text' || field.type === 'textarea') {
    // Determine input type based on validation
    if (field.validation?.email) {
      schema.inputType = 'email'
    } else if (field.validation?.url) {
      schema.inputType = 'url'
    } else if (field.validation?.numeric) {
      schema.inputType = 'number'
    }
  }

  // Handle options for select/multiselect/radio
  if (
    (field.type === 'select' ||
      field.type === 'multiselect' ||
      field.type === 'radio') &&
    field.options
  ) {
    schema.items = field.options.map((opt) => ({
      value: opt.value,
      label: opt.label,
    }))
  }

  // Handle checkbox
  if (field.type === 'checkbox') {
    schema.text = field.label
  }

  // Handle conditional display
  if (field.conditional) {
    // Vueform condition syntax: field_name == value
    const operator = field.conditional.operator === 'equals' ? '==' : '!='
    schema.conditions = `${field.conditional.field} ${operator} ${JSON.stringify(field.conditional.value)}`
  }

  return schema
}

/**
 * Convert multiple FormBuilderFields to Vueform schema
 */
export function convertFieldsToSchema(fields: FormBuilderField[]): VueformSchema {
  const schema: VueformSchema = {}

  // Sort by order
  const sortedFields = [...fields].sort((a, b) => a.order - b.order)

  for (const field of sortedFields) {
    if (!field.hidden) {
      schema[field.name] = convertToVueformField(field)
    }
  }

  return schema
}

/**
 * Generate unique field ID
 */
export function generateFieldId(): string {
  return `field_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Reconstruct FormBuilderField[] from a Vueform schema.
 * Used to hydrate the editor when an existing vueformConfig is loaded.
 */
export function convertSchemaToFields(schema: VueformSchema): FormBuilderField[] {
  return Object.entries(schema).map(([name, fieldSchema], index) => {
    const field: FormBuilderField = {
      id: generateFieldId(),
      name,
      type: fieldSchema.type,
      label: fieldSchema.label || name,
      order: index,
    }

    if (fieldSchema.placeholder) field.placeholder = fieldSchema.placeholder
    if (fieldSchema.help) field.helpText = fieldSchema.help
    if (fieldSchema.default !== undefined) field.defaultValue = fieldSchema.default
    if (fieldSchema.onUpgrade) field.onUpgrade = fieldSchema.onUpgrade
    if (fieldSchema.readonly) field.readonly = fieldSchema.readonly
    if (typeof fieldSchema.columns === 'number') field.columns = fieldSchema.columns

    // Parse rules string back into FieldValidationConfig
    if (fieldSchema.rules) {
      const rulesStr = Array.isArray(fieldSchema.rules)
        ? fieldSchema.rules.join('|')
        : fieldSchema.rules
      const validation: FieldValidationConfig = {}
      let hasValidation = false

      for (const rule of rulesStr.split('|')) {
        if (rule === 'required')  { validation.required = true;  hasValidation = true }
        else if (rule === 'email')   { validation.email = true;    hasValidation = true }
        else if (rule === 'url')     { validation.url = true;      hasValidation = true }
        else if (rule === 'numeric') { validation.numeric = true;  hasValidation = true }
        else if (rule.startsWith('min:')) {
          const v = parseInt(rule.slice(4))
          if (!isNaN(v)) { validation.minLength = v; hasValidation = true }
        } else if (rule.startsWith('max:')) {
          const v = parseInt(rule.slice(4))
          if (!isNaN(v)) { validation.maxLength = v; hasValidation = true }
        }
      }

      if (hasValidation) {
        field.validation = validation
        if (validation.required) field.required = true
      }
    }

    // Reconstruct options for select / multiselect / radio
    if (
      fieldSchema.items &&
      (field.type === 'select' || field.type === 'multiselect' || field.type === 'radio')
    ) {
      field.options = (fieldSchema.items as any[]).map((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return { value: String(item), label: String(item) }
        }
        return { value: String(item.value), label: String(item.label) }
      })
    }

    return field
  })
}
